/**
 * Typed jobs API facade over generated contract clients.
 */

import {
  fingerprintOverrideBodySchema,
  InitiateBatchScanRequestSchema,
  initiateContainerSlotScanJobRequestSchema,
  jobImportPreviewRequestSchema,
  jobsActiveCheckResponseSchema,
  jobImportPreviewResponseSchema,
  retryContainerSlotScanOcrRequestSchema,
  tenantJobsActiveListResponseSchema,
  tenantJobsListQuerySchema,
  tenantJobsListResponseSchema,
  type InitiateBatchScanRequest,
  type RetryOcrRequest,
  type UnifiedJobProgress,
} from '@cnapse/shared/schemas.js';
import { z } from 'zod';
import { containerSlotScansApi } from '../api/generated/containerSlotScans.api.js';
import { jobsApi } from '../api/generated/jobs.api.js';

class JobsService {
  async getJobs(
    query?: Partial<z.infer<typeof tenantJobsListQuerySchema>>,
  ): Promise<z.infer<typeof tenantJobsListResponseSchema>> {
    return jobsApi.list(query ?? {}) as Promise<z.infer<typeof tenantJobsListResponseSchema>>;
  }

  async getJobCreatableTypes(): Promise<Array<'container_slot_scan' | 'batch_wafer_transfer'>> {
    return jobsApi.getCreatableTypes() as Promise<Array<'container_slot_scan' | 'batch_wafer_transfer'>>;
  }

  async getJobDetail(jobId: string, jobType?: string): Promise<unknown> {
    return jobsApi.getDetail(jobId, { jobType });
  }

  async deleteJob(jobId: string, jobType: string): Promise<void> {
    await jobsApi.delete(jobId, {
      jobType: jobType as 'container_slot_scan' | 'batch_wafer_transfer',
    });
  }

  async getJobsActiveCheck(query: {
    containerIds?: number[] | string;
    efemClientId?: number;
    gatewaySerialNumber?: string;
    loadPortIds?: number[] | string;
  }): Promise<z.infer<typeof jobsActiveCheckResponseSchema>> {
    const params: Record<string, string | number | undefined> = {};
    const containerIds = this.normalizeActiveCheckIdList(query.containerIds);
    if (containerIds.length > 0) {
      params.containerIds = containerIds.join(',');
    }
    if (query.efemClientId != null) {
      params.efemClientId = query.efemClientId;
    }
    if (query.gatewaySerialNumber) {
      params.gatewaySerialNumber = query.gatewaySerialNumber;
    }
    const loadPortIds = this.normalizeActiveCheckIdList(query.loadPortIds);
    if (loadPortIds.length > 0) {
      params.loadPortIds = loadPortIds.join(',');
    }
    return jobsApi.activeCheck(params) as Promise<z.infer<typeof jobsActiveCheckResponseSchema>>;
  }

  private normalizeActiveCheckIdList(value: number[] | string | undefined): number[] {
    if (value === undefined) {
      return [];
    }
    const raw = Array.isArray(value) ? value : value.split(',');
    return raw
      .map((x) => (typeof x === 'number' ? x : Number.parseInt(String(x).trim(), 10)))
      .filter((n) => Number.isFinite(n) && n > 0);
  }

  async previewJobImport(
    body: z.infer<typeof jobImportPreviewRequestSchema>,
  ): Promise<z.infer<typeof jobImportPreviewResponseSchema>> {
    return jobsApi.importPreview(body) as Promise<z.infer<typeof jobImportPreviewResponseSchema>>;
  }

  async setFingerprintOverride(
    jobId: string,
    slotNumber: number,
    body: z.infer<typeof fingerprintOverrideBodySchema>,
  ): Promise<void> {
    const validated = fingerprintOverrideBodySchema.parse(body);
    await containerSlotScansApi.setFingerprintOverride({ jobId, slotNumber }, validated);
  }

  async getUnifiedJobProgress(jobId: string, jobType?: string): Promise<UnifiedJobProgress> {
    return jobsApi.getProgress(jobId, { jobType }) as Promise<UnifiedJobProgress>;
  }

  async listActiveJobs(options?: {
    view?: 'mine' | 'all';
    type?: 'container_slot_scan' | 'batch_wafer_transfer' | 'all';
    limit?: number;
  }): Promise<z.infer<typeof tenantJobsActiveListResponseSchema>['data']> {
    return jobsApi.listActive({
      view: options?.view ?? 'mine',
      type: options?.type ?? 'all',
      limit: options?.limit,
    }) as Promise<z.infer<typeof tenantJobsActiveListResponseSchema>['data']>;
  }

  async abortJob(
    jobId: string,
    jobType: 'container_slot_scan' | 'batch_wafer_transfer',
    reason?: string,
  ): Promise<void> {
    await jobsApi.abort(jobId, { jobType }, reason ? { reason } : {});
  }

  async retryBatchScanOcr(
    jobId: string,
    request: RetryOcrRequest,
    options?: { force?: boolean },
  ): Promise<void> {
    const validatedRequest = retryContainerSlotScanOcrRequestSchema.parse(request);
    await containerSlotScansApi.retryOcr(jobId, { force: options?.force }, validatedRequest);
  }

  async initiateBatchScan(request: InitiateBatchScanRequest): Promise<string> {
    const validatedRequest = InitiateBatchScanRequestSchema.parse(request);
    const { mapBatchScanInitiateToContainerSlotScan } = await import(
      '../utils/containerSlotScanProgressAdapter.js'
    );
    const body = initiateContainerSlotScanJobRequestSchema.parse(
      mapBatchScanInitiateToContainerSlotScan(validatedRequest),
    );
    return containerSlotScansApi.initiate(body) as Promise<string>;
  }

  /** @alias initiateBatchScan */
  async initiateOperationsSlotScan(request: InitiateBatchScanRequest): Promise<string> {
    return this.initiateBatchScan(request);
  }
}

export const jobsService = new JobsService();
export { JobsService };
